/**
 * foodCategories.js
 *
 * Shared multi-category membership helpers for the Food model's additive
 * `categories` array (ordered, first entry = primary; legacy `category`
 * scalar always mirrors `categories[0]`, see backend `Food.model.js`).
 * Every reader that groups/filters foods by category id MUST go through
 * these helpers instead of reading `food.category`/`food.categories`
 * directly — `categories` is NOT guaranteed to exist on old documents (the
 * backfill migration is written but not run), so a bare `food.categories`
 * read silently returns `undefined` for most of the current catalog.
 *
 * @module helpers/foodCategories
 */

/** Unwrap one category ref — a populated `{_id}` object, an ObjectId, or a
 * bare string — down to its id string. */
const idOf = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'object') return String(value._id || value.id || value.value || value);
    return String(value);
};

/**
 * Resolve every category id a food belongs to, as strings, in the food's own
 * order (primary first).
 *
 * - `categories` non-empty wins outright (mirrors it verbatim, unwrapped).
 * - Otherwise falls back to `[category]` when `category` is set.
 * - Otherwise `[]` (uncategorised).
 *
 * @param {object} food a raw or populated food document.
 * @returns {string[]} ordered category id strings (possibly empty).
 */
export const foodCategoryIds = (food) => {
    const categories = Array.isArray(food?.categories) ? food.categories : [];
    if (categories.length) {
        return categories.map(idOf).filter(Boolean);
    }
    const primary = idOf(food?.category);
    return primary ? [primary] : [];
};

/**
 * Whether `food` belongs to the given category id — `categories` membership
 * when present, else a single `category` equality check.
 *
 * @param {object} food a raw or populated food document.
 * @param {string} categoryId the category id to test membership against.
 * @returns {boolean}
 */
export const foodBelongsToCategory = (food, categoryId) => {
    const id = idOf(categoryId);
    if (!id) return false;
    return foodCategoryIds(food).includes(id);
};
